
import { Options, Vue } from "vue-class-component";
import { formatByCountry, parseByCountry } from "@/app/infrastructures/misc/Utils";
import {
  CargoDetailStt,
  CargoInputData,
  DimensionPerKoliData
} from "@/domain/entities/Cargo";
import { storeDatatoSessionStorage } from "@/app/infrastructures/misc/SessionStorage";
import { CargoController } from "@/app/ui/controllers/CargoController";
import { gtmCargoData } from "@/app/ui/views/cargo/modules/gtm";
import { dataLayer } from "@/app/infrastructures/misc/UtilsGtm";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { ReadyToCargoController } from "@/app/ui/controllers/ReadyToCargoController";
import { CalculateVolumeApiRequest, CalculateVolumeRequest } from '@/data/payload/api/CargoApiRequest';
import { CALCULATE_TYPE, getConfigDimensionDivider, getConfigIsminimumVolumePerPiece, getConfigMinimumVolumePerPiece } from '../../modules/cargo-module';
import { FlagsPermissionCargo } from '@/feature-flags/flags-permission-cargo';

@Options({})
export default class CargoTableSttSection extends Vue {
  mounted() {
    this.updateEstimationVolumeAndWeight();
    window.addEventListener("delete-stt", this.handleDeleteStt)
  }

  unmounted() {
    window.removeEventListener("delete-stt", this.handleDeleteStt)
  }

 async  handleDeleteStt() {
    this.itemToDelete = CargoController.itemToDelete
    await this.$nextTick();
    CargoController.setCacheCommodityGroup(true);
    await this.$nextTick();
    await this.isDeleteCargoItem();
    this.setSuccessModalDelete(false);
  }

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  convertDecimal(value: any) {
    return formatByCountry(Number(value), { type: "weight", country: this.countryCode });
  }

  get formBooking() {
    return CargoController.form;
  }

  get isCargoPlane() {
    return this.formBooking.cargoType === "plane";
  }

  get isCargoPlaneAuto() {
    return (
      this.formBooking.cargoType === "plane" &&
      this.formBooking.cargoTypeStatus === "auto"
    );
  }

  get isEditRtc() {
    return ReadyToCargoController.flowBooking === "rtc-edit";
  }

  get sttResult(): CargoDetailStt[] {
    return CargoController.sttResultFinal;
  }

  get bagResult(): {
    baggingNumber: string;
    bagGrossWeight: number;
    stt: CargoDetailStt[];
  }[] {
    return CargoController.bagResult;
  }

  get totalStt() {
    return this.sttResult.length;
  }

  get totalSttPieces() {
    return CargoController.totalSttPieces;
  }

  get form() {
    return CargoController.form;
  }

  // airport
  get isPlaneAuto() {
    return (
      this.form.cargoType === "plane" && this.form.cargoTypeStatus === "auto"
    );
  }

  get sourceSttFromLocalStorage() {
    return CargoController.sourceSttFromLocalStorage;
  }

  get totalGrossWeights() {
    return CargoController.totalGrossWeights || "";
  }

  get totalVolumeWeights() {
    return CargoController.totalVolumeWeights;
  }

  pagination = {
    page: 1,
    limit: 50
  };

  get columns() {
    return [
      {
        name: "No.",
        styleHead: "w-10 text-center whitespace-no-wrap",
        styleCustom: "align-center",
        render: (_: any, index: number) => {
          return `<div class="text-black-lp-300">${index +
            1 +
            this.pagination.limit * (this.pagination.page - 1)}</div>`;
        }
      },
      {
        name: "No. bag",
        styleHead: "w-40 text-left whitespace-no-wrap",
        render: (item: CargoDetailStt) => {
          return `<div class="text-black-lp-300 whitespace-no-wrap">
                  ${item.baggingVendorNo || item.baggingNumber || "-"}
                </div>`;
        }
      },
      {
        name: "Chargeable bag",
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: CargoDetailStt) => {
          return `<div class="text-black-lp-300 whitespace-no-wrap">
              ${
                item.bagCustomChargeableWeight
                  ? formatByCountry(item.bagCustomChargeableWeight, { type: "weight", country: this.countryCode }) + " kg"
                  : "-"
              }
            </div>`;
        }
      },
      {
        name: "No. STT",
        styleHead: "w-40 text-left whitespace-no-wrap",
        render: (item: any) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const x in item.sttDetails) {
              html += `<div class="text-black-lp-300">${item.sttDetails[x].sttNumber}</div>`;
            }
            html += `</div>`;
            return html;
          }
          return `<div class="text-black-lp-300 whitespace-no-wrap">
                  ${item.sttNumber}
                </div>`;
        }
      },
      {
        name: this.$t("No. referensi"),
        styleHead: "w-40 text-left whitespace-no-wrap",
        render: (item: any) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const x in item.sttDetails) {
              html += `<div class="text-black-lp-300">${item.sttDetails[x]
                .sttNoRefExternal || "-"}</div>`;
            }
            html += `</div>`;
            return html;
          }
          return `<div class="text-black-lp-300 whitespace-no-wrap">
                  ${item.sttNoRefExternal || "-"}
                </div>`;
        }
      },
      {
        name: "Chargeable weight STT",
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: any) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const x in item.sttDetails) {
              html += `<div class="text-black-lp-300">${formatByCountry(item.sttDetails[x].chargeableWeight, { type: "weight", country: this.countryCode })} kg</div>`;
            }
            html += `</div>`;
            return html;
          }
          return `<div class="text-black-lp-300 whitespace-no-wrap">
                  ${formatByCountry(item.chargeableWeight, { type: "weight", country: this.countryCode })} kg
                </div>`;
        }
      },
      {
        name: this.$t("Berat kotor STT"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: any) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const x in item.sttDetails) {
              html += `<div class="text-black-lp-300">${formatByCountry(item.sttDetails[x].grossWeigth, { type: "weight", country: this.countryCode })} kg</div>`;
            }
            html += `</div>`;
            return html;
          }
          return `<div class="text-black-lp-300 whitespace-no-wrap">
                  ${formatByCountry(item.grossWeigth, { type: "weight", country: this.countryCode })} kg
                </div>`;
        }
      },
      {
        name: this.$t("Berat volume STT"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: any) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const x in item.sttDetails) {
              html += `<div class="text-black-lp-300">${formatByCountry(item.sttDetails[x].volumeWeight, { type: "weight", country: this.countryCode })} kg</div>`;
            }
            html += `</div>`;
            return html;
          }
          return `<div class="text-black-lp-300 whitespace-no-wrap">
                  ${formatByCountry(item.volumeWeight, { type: "weight", country: this.countryCode })} kg
                </div>`;
        }
      },
      {
        name: this.$t("Koli"),
        styleHead: "w-24 text-left whitespace-no-wrap",
        render: (item: any) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const x in item.sttDetails) {
              html += `<div class="text-black-lp-300">${item.sttDetails[x].totalPieces}</div>`;
            }
            html += `</div>`;
            return html;
          }
          return `<div class="text-black-lp-300 whitespace-no-wrap">
              ${item.totalPieces}
            </div>`;
        }
      },
      {
        name: this.$t("Jenis pengiriman"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: any) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const x in item.sttDetails) {
              html += `<div class="text-black-lp-300 rounded px-2 py-0 bg-gray-lp-400">${item.sttDetails[x].productType}</div>`;
            }
            html += `</div>`;
            return html;
          }
          return `<div class="text-black-lp-300 whitespace-no-wrap rounded px-2 py-0 bg-gray-lp-400">
                  ${item.productType}
                </div>`;
        }
      },
      {
        name: this.$t("Komoditas"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: any) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const x in item.sttDetails) {
              html += `<div class="text-black-lp-300">${item.sttDetails[x].commodity}</div>`;
            }
            html += `</div>`;
            return html;
          }
          return `<div class="text-black-lp-300 whitespace-no-wrap">
                  ${item.commodity}
                </div>`;
        }
      },
      {
        name: "Kota tujuan",
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: any) => {
          let html = `<div class="space-y-3">`;
          if (item.sttDetails) {
            for (const x in item.sttDetails) {
              html += `<div class="text-black-lp-300 rounded px-2 py-0 bg-gray-lp-400">${
                this.isEditRtc
                  ? item.destinationCityCode
                  : item.sttDetails[x].destinationCityCode
              }</div>`;
            }
            html += `</div>`;
            return html;
          }
          return `<div class="text-black-lp-300 whitespace-no-wrap rounded px-2 py-0 bg-gray-lp-400">
                  ${item.destinationCityCode}
                </div>`;
        }
      },
      {
        name: "Atur",
        key: "actionable_column",
        styleHead: "w-36 text-left whitespace-no-wrap",
        showButton: () => {
          return true;
        }
      }
    ].filter(Boolean);
  }

  get isAccountForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  customButton = {
    outline: true,
    iconLeft: "trash-red",
    title: this.isAccountForeign ? "Delete" : "Hapus",
    textColor: "red-lp-100",
    color: "red-lp-100",
    small: false,
    customClass: "whitespace-no-wrap",
    disabled: false,
    clickFunction: (item: any) => {
      if (!item.baggingNumber) {
        this.wordingDelete =
          "STT yang Anda hapus akan hilang dari daftar pengiriman";
      } else {
        this.wordingDelete =
          "Semua STT dari No. Bag yang sama akan terhapus dari daftar pengiriman";
      }
      this.itemToDelete = item;
      this.actionModalDelete(true);
      CargoController.setCacheCommodityGroup(true);
    }
  };

  // popup delete
  get isShowPopup(): boolean {
    return this.successModalDelete || this.isShowModalDeleteConfirmation;
  }

  wordingDelete = "";
  itemToDelete: any = "";
  isShowModalDeleteConfirmation = false;
  isUnpaid = false;
  successModalDelete = false;

  setSuccessModalDelete(bool: boolean) {
    this.successModalDelete = bool;
  }

  actionModalDelete(bool: boolean) {
    this.isShowModalDeleteConfirmation = bool;
    this.isUnpaid = false;
  }

  async isDeleteCargoItem() {
    if (this.itemToDelete.sttDetails) {
      this.deleteSttResultByBagNumber(
        this.itemToDelete.baggingNumber || this.itemToDelete.baggingVendorNo
      );
    } else {
      this.deleteSttResultBySttNumber(this.itemToDelete.sttNumber);
    }
    this.actionModalDelete(false);
    this.setSuccessModalDelete(true);
    CargoController.setFillForm(true);
    CargoController.setIsDeleteStt(true);

    this.isUnpaid = false;
    const res = isNaN(+this.totalGrossWeights)
      ? parseByCountry(this.totalGrossWeights, { country: this.countryCode }) < 10
      : +this.totalGrossWeights < 10;
    CargoController.setNotificationWeight(this.totalGrossWeights && res);
    CargoController.mappingCommodityOptions();

    this.updateDimensionPerKoliData();
    window.dispatchEvent(new Event("deletecargostt"));
    await this.$nextTick();
    this.form.actualTotalSTTPieces = CargoController.totalSttPieces;
    CargoController.setOnChangeTotalColi(CargoController.totalSttPieces);
    await this.$nextTick();
    CargoController.setOnChangeTotalActualGrossWeight(
      CargoController.totalNewCalculationGrossWeights
    );
    CargoController.setEstimationTotalGrossWeight(
      CargoController.totalNewCalculationGrossWeights
    );
    CargoController.setEstimationTotalVolumeWeight(
      Math.round(CargoController.totalNewCalculationVolumeWeights)
    );
    this.form.estTotalCargoGrossWeight = this.convertDecimal(
      CargoController.totalNewCalculationGrossWeights
    );
    this.form.estTotalCargoVolumeWeight = this.convertDecimal(
      Math.round(CargoController.totalNewCalculationVolumeWeights)
    );
    this.calculateVolume(CALCULATE_TYPE.INIT);
  }

  deleteSttResultByBagNumber(bagNumber: string) {
    const deletedBagging = this.bagResult.filter(
      (item: { baggingNumber: string; stt: CargoDetailStt[] }) => {
        return item.baggingNumber === bagNumber;
      }
    )[0];
    this.$emit("dataChange");
    CargoController.setSTTResultFinal(
      this.sttResult.filter((item: CargoDetailStt) => {
        return item.baggingNumber !== bagNumber;
      })
    );
    storeDatatoSessionStorage("cargo", this.sttResult);
    CargoController.setSourceFromLocalStorage(this.sttResult);
    CargoController.setBagResult(
      this.bagResult.filter(
        (item: { baggingNumber: string; stt: CargoDetailStt[] }) => {
          return item.baggingNumber !== bagNumber;
        }
      )
    );
    const totalActualGrossWeight =
      this.cargoInputForm.calcuateActualCargoGrossWeight +
      -(deletedBagging?.bagGrossWeight ?? this.itemToDelete.grossWeigth); // explanation https://github.com/Lionparcel/medusa/pull/5753/files#r1285584448
    CargoController.setTotalActualGrossWeight(totalActualGrossWeight);
    CargoController.resetTotalColiOnLocalStorage();
    this.onChangeTotalColi(CargoController.totalSttPieces);
    CargoController.setTotalPcs(CargoController.totalSttPieces);

    this.updateEstimationVolumeAndWeight();
    this.calculationVolumeWeight();
    storeDatatoSessionStorage("cargo-bag", this.bagResult);
    if (!this.sttResult.length) {
      CargoController.setResetDestinationCity();
      CargoController.resetCargoInputForm(new CargoInputData());
      CargoController.setFormDetailPieces({
        totalPieces: 0,
        dimensionPieces: [
          {
            pieces: 0,
            length: 30,
            width: 20,
            height: 10,
            grossWeight: 1
          }
        ]
      });
      CargoController.setHaveBeenSubmit(false);
    }
  }

  deleteSttResultBySttNumber(sttNumber: string) {
    this.$emit("dataChange");
    CargoController.setSTTResultFinal(
      this.sttResult.filter((item: CargoDetailStt) => {
        return item.sttNumber !== sttNumber;
      })
    );
    const newSttResult = this.sourceSttFromLocalStorage.filter((item: any) => {
      return item.sttNumber !== sttNumber;
    });
    storeDatatoSessionStorage("cargo", newSttResult);
    CargoController.setSourceFromLocalStorage(newSttResult);
    const totalActualGrossWeight =
      this.cargoInputForm.calcuateActualCargoGrossWeight +
      -this.itemToDelete.grossWeigth; // explanation https://github.com/Lionparcel/medusa/pull/5753/files#r1285584448
    CargoController.setTotalActualGrossWeight(totalActualGrossWeight);
    CargoController.resetTotalColiOnLocalStorage();
    this.onChangeTotalColi(CargoController.totalSttPieces);
    CargoController.setTotalPcs(CargoController.totalSttPieces);
    CargoController.mappingCommodityOptions();
    this.updateEstimationVolumeAndWeight();
    this.calculationVolumeWeight();
    if (!this.sttResult.length) {
      CargoController.setResetDestinationCity();
      CargoController.resetCargoInputForm(new CargoInputData());
      CargoController.setFormDetailPieces({
        totalPieces: 0,
        dimensionPieces: [
          {
            pieces: 0,
            length: 30,
            width: 20,
            height: 10,
            grossWeight: 1
          }
        ]
      });
      CargoController.setHaveBeenSubmit(false);
    }
  }

  onChangeTotalColi(value: number) {
    CargoController.setOnChangeTotalColi(value);
  }

  calculationVolumeWeight() {
    CargoController.calculationVolumeWeight(CargoController.totalSttPieces);
  }

  set setEstimationTotalVolumeWeight(value: any) {
    CargoController.setEstimationTotalVolumeWeight(value);
  }

  set setEstimationTotalGrossWeight(value: any) {
    CargoController.setEstimationTotalGrossWeight(value);
  }

  updateEstimationVolumeAndWeight() {
    this.setEstimationTotalVolumeWeight = this.totalVolumeWeights;
    this.setEstimationTotalGrossWeight = this.totalGrossWeights;
  }

  get cargoInputForm() {
    return CargoController.cargoInputForm;
  }

  get popupModalData() {
    // if success delete
    if (this.successModalDelete) {
      // GTM
      if (this.formBooking.cargoType && this.formBooking.cargoType !== "ship") {
        const gtmDeleteSttSuccess = gtmCargoData({
          type: this.formBooking.cargoType,
          isAuto: this.isCargoPlaneAuto
        }).deleteSttSuccess;

        dataLayer(
          gtmDeleteSttSuccess.eventName,
          gtmDeleteSttSuccess.property,
          []
        );
      }
      return {
        success: {
          action: () => {
            this.setSuccessModalDelete(false);
          },
          text: "OK"
        },
        title: this.$t("STT Berhasil Dihapus"),
        message: this.$t(
          "Anda dapat menambahkan STT baru ke daftar pengiriman"
        ),
        image: "image-modal-success"
      };
    }
    // show popup delete
    return {
      success: {
        action: () => this.isDeleteCargoItem(),
        text: this.$t("Hapus")
      },
      cancel: {
        action: () => this.actionModalDelete(false),
        text: this.$t("Batal")
      },
      title: this.$t("Hapus STT"),
      message: `${this.$t(this.wordingDelete)}`,
      image: "badge-confirmation-general"
    };
  }

  updateDimensionPerKoliData() {
    CargoController.setIsSttUpdated(CargoController.isDimensionSaved);

    const {
      sttNumber,
      baggingNumber,
      baggingVendorNo,
      baggingVendorNumber
    } = this.itemToDelete;

    const newDimensionData = CargoController.dimensionPerKoliData.dimensionPieces.filter(
      (item) =>
        !(
          (sttNumber && item.sttOrBag.includes(sttNumber)) ||
          (baggingNumber && item.sttOrBag.includes(baggingNumber)) ||
          (baggingVendorNo && item.sttOrBag.includes(baggingVendorNo)) ||
          (baggingVendorNumber && item.sttOrBag.includes(baggingVendorNumber))
        )
    );

    CargoController.setDimensionPerKoliData(
      new DimensionPerKoliData({
        dimensionPieces: newDimensionData
      })
    );
  }

  parseNumber(value: string) {
    if (typeof value === "number") return value;
    return parseByCountry(value, { country: this.countryCode });
  }

  get totalNewCalculationGrossWeights() {
    return CargoController.totalNewCalculationGrossWeights;
  }

  get totalNewCalculationVolumeWeights() {
    return CargoController.totalNewCalculationVolumeWeights;
  }

  get chargeableWeight() {
    return Math.max(
      1,
      this.parseNumber(String(this.totalNewCalculationGrossWeights)),
      this.parseNumber(String(this.totalNewCalculationVolumeWeights))
    );
  }

  async calculateVolume(calculateType = ""){
    await this.$nextTick();
    CargoController.calculateVolume(new CalculateVolumeApiRequest({
      calculateType,
      calculateRequest: new CalculateVolumeRequest({
        cargoType: this.form.cargoType,
        chargeableWeightReweigh: this.chargeableWeight,
        currentGrossWeight: this.parseNumber(this.form.actualTotalCargoGrossWeight),
        height: this.form.actualDimension.height,
        length: calculateType === "INIT" ? 90 : this.form.actualDimension.length,
        width: calculateType === "INIT" ? 60 : this.form.actualDimension.width,
        totalPieces: this.form.actualTotalSTTPieces,
        volumeWeightReweigh: this.parseNumber(this.totalNewCalculationVolumeWeights as any),
        divider: getConfigDimensionDivider(this.form.cargoType),
        isMinimumVolumePerPiece: getConfigIsminimumVolumePerPiece(this.form.cargoType, this.form.cargoTypeStatus),
        minVolumePerPiece: getConfigMinimumVolumePerPiece(this.form.cargoType, this.form.cargoTypeStatus)
      })
    }))
  }
}
